import { useForm, zodResolver } from "@mantine/form";

import {
  LABOR_QUESTIONNAIRE_DISPLAY_NAMES,
  QuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/labor-questionnaires";
import { LaborStageSelectField } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/LaborStageSelectField";
import { PositionFormFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/components/PositionFormFields";
import {
  POSITION_DATE_LINK_ID,
  POSITION_DATE_TIME_LINK_ID,
  POSITION_QUESTIONNAIRE_NAME,
  POSITION_ROOT_LINK_ID,
  POSITION_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/position/constants/position-link-ids";
import type { PositionFlowsheetDrawerProps } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/types/position-drawer";
import type { PositionFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/types/position-form";
import {
  getDefaultPositionFlowsheetFormValues,
  normalizePositionFlowsheetFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/position/utils/position-form-defaults";
import {
  mapPositionFormValuesToQuestionnaireValues,
  mapQuestionnaireResponseToPositionFormValues,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/position/utils/position-questionnaire-mapping";
import { positionFlowsheetFormSchema } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/zod-schemas/position-flowsheet-form";
import {
  LABOR_FLOWSHEET_DRAWER_MODES,
  LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-drawer-constants";
import { LaborFlowsheetDrawerShell } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDrawerShell";
import {
  type LaborFlowsheetDrawerEditContext,
  useLaborFlowsheetDrawerPersist,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/useLaborFlowsheetDrawerPersist";

const POSITION_DISPLAY_NAME = LABOR_QUESTIONNAIRE_DISPLAY_NAMES[QuestionnaireType.Position];

function getPositionEditContext(
  props: PositionFlowsheetDrawerProps,
): LaborFlowsheetDrawerEditContext | undefined {
  if (props.mode !== LABOR_FLOWSHEET_DRAWER_MODES.Edit) {
    return undefined;
  }

  return {
    observation: props.observation,
    questionnaireDisplayName: props.questionnaireDisplayName,
  };
}

function mergePositionHydratedEditFormValues({
  mapped,
  hydratedQuestionnaireValues,
}: {
  mapped: PositionFlowsheetFormValues;
  hydratedQuestionnaireValues: Record<string, unknown>;
}): PositionFlowsheetFormValues {
  const hydratedRoot = hydratedQuestionnaireValues[POSITION_ROOT_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedDateTime = hydratedRoot?.[POSITION_DATE_TIME_LINK_ID] as
    | Record<string, unknown>
    | undefined;
  const hydratedPositionDate = hydratedDateTime?.[POSITION_DATE_LINK_ID];
  const hydratedPositionTime = hydratedDateTime?.[POSITION_TIME_LINK_ID];

  return {
    ...mapped,
    positionDate: hydratedPositionDate instanceof Date ? hydratedPositionDate : mapped.positionDate,
    positionTime:
      typeof hydratedPositionTime === "string" ? hydratedPositionTime : mapped.positionTime,
  };
}

export function PositionFlowsheetDrawer(props: PositionFlowsheetDrawerProps) {
  const { opened, onClose, clientId, encounterId, mode } = props;

  const form = useForm<PositionFlowsheetFormValues>({
    initialValues: getDefaultPositionFlowsheetFormValues(),
    validate: zodResolver(positionFlowsheetFormSchema),
  });

  const {
    isLoading,
    isSubmitting,
    loadError,
    dateTimeUtcDateAnchors,
    drawerLaborStageLoincCode,
    setDrawerLaborStageLoincCode,
    showLaborStageInDrawer,
    drawerTitle,
    handleSubmit,
  } = useLaborFlowsheetDrawerPersist<PositionFlowsheetFormValues>({
    opened,
    mode,
    onClose,
    clientId,
    encounterId,
    questionnaireName: POSITION_QUESTIONNAIRE_NAME,
    displayName: POSITION_DISPLAY_NAME,
    timerKey: "position",
    form,
    parseAndNormalizeFormValues: (values) =>
      normalizePositionFlowsheetFormValues(positionFlowsheetFormSchema.parse(values)),
    getDefaultFormValues: getDefaultPositionFlowsheetFormValues,
    mapFormValuesToQuestionnaireValues: mapPositionFormValuesToQuestionnaireValues,
    mapQuestionnaireResponseToFormValues: mapQuestionnaireResponseToPositionFormValues,
    mergeHydratedEditFormValues: mergePositionHydratedEditFormValues,
    questionnaireNotFoundMessage: "Position questionnaire not found.",
    editEntryNotFoundMessage: "Could not load existing position entry.",
    editContext: getPositionEditContext(props),
    onRefresh: props.mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd ? props.onRefresh : undefined,
    title: props.title,
  });

  return (
    <LaborFlowsheetDrawerShell
      opened={opened}
      onClose={onClose}
      error={loadError}
      title={drawerTitle}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      dateTimeUtcDateAnchors={dateTimeUtcDateAnchors}
      formId={`labor-flowsheet-form-${QuestionnaireType.Position}`}
      submitButtonText={
        mode === LABOR_FLOWSHEET_DRAWER_MODES.Edit
          ? LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT.Update
          : LABOR_FLOWSHEET_SUBMIT_BUTTON_TEXT.AddToFlowsheet
      }
      headerSection={
        showLaborStageInDrawer && drawerLaborStageLoincCode ? (
          <LaborStageSelectField
            value={drawerLaborStageLoincCode}
            onChange={setDrawerLaborStageLoincCode}
          />
        ) : null
      }
    >
      <PositionFormFields form={form} mode={mode} clientId={clientId} isDrawerOpened={opened} />
    </LaborFlowsheetDrawerShell>
  );
}
